import {
  Control,
  UseFormGetValues,
  UseFormRegister,
  Controller,
  FieldErrors,
  FieldArrayWithId,
  UseFormSetValue,
} from 'react-hook-form'
import { FormInput } from '../../../../../components/forms/input/FormInput'
import { FormSelect } from '../../../../../components/forms/select/FormSelect'
import {
  useLazyRecuperarUsuarioAutocompleteQuery,
  useRecuperarOpcCatalogoQuery,
} from '../../../../../redux/api'
import { AppCodigosCatalogo, AppConstantes } from '../../../../../utils/Constantes'
import { FormReactDatePicker } from '../../../../../components/forms/datePicker/FormReactDatePicker'
import { FormSelectAutocomplete } from '../../../../../components/forms/select/FormSelectAutocomplete'
import { OpcionUsuarioSelect } from '../../../../../components/forms/select/OpcionesSelect'
import { TCondicionanteForm } from '../../../models/condicionanteForm.type'
import { Condicionante } from '../../../models'
import { Usuario } from '../../../../usuarios/models'
import dayjs from '../../../../../utils/DayjsUtil'
type Props = {
  idSitio?: string
  fechaVencimiento?: string
  condicionante?: Condicionante
  control: Control<TCondicionanteForm>
  register: UseFormRegister<TCondicionanteForm>
  errors: FieldErrors<TCondicionanteForm>
  getValues: UseFormGetValues<TCondicionanteForm>
  setValue: UseFormSetValue<TCondicionanteForm>
  index: number
  field: FieldArrayWithId<TCondicionanteForm, 'tareas'>
  alertTareaCancelada: (arg: { valor: string; etiqueta: string; ext5: string | null }) => void
  usuarioPermiso?: boolean
  recalcularFechasInicio: () => void
}
export const TareaCondicionanteModalCamposTareas = ({
  idSitio,
  control,
  register,
  errors,
  getValues,
  setValue,
  fechaVencimiento,
  index,
  field,
  alertTareaCancelada,
  usuarioPermiso,
  recalcularFechasInicio,
}: Props) => {
  // Campos de captura por tarea (inline) dentro del modal.
  // Reglas UI:
  // - Estatus BAJA deshabilita edicion.
  // - fechaTermino tiene maxDate=fechaVencimiento de la autorizacion (si aplica).
  // - Para tareas autogeneradas (isManual=false), el titulo se sincroniza con la fechaTermino.
  const { data: catEstatusTarea } = useRecuperarOpcCatalogoQuery(AppCodigosCatalogo.TAREAS_ESTATUS)
  const [recuperarUsuarios] = useLazyRecuperarUsuarioAutocompleteQuery()

  /**
   * @description Agrega las opciones al select de autocompletado
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 07/12/2022
   * @param entradaBusqueda
   * @param callback Regresa el arreglo de opciones del select
   */
  const agregaOpcionesSelectUsuario = (entradaBusqueda: string, callback: Function) => {
    // Autocomplete de usuarios limitado por sitio.
    if (entradaBusqueda && entradaBusqueda.length >= 2) {
      recuperarUsuarios({ nombre: entradaBusqueda, idSitio: idSitio })
        .unwrap()
        .then((resp: Usuario[]) => {
          callback(
            resp?.map((usuario: Usuario) => ({
              etiqueta: `${usuario.nombre} ${usuario.apellidoPaterno} ${usuario.apellidoMaterno}`,
              valor: usuario.idUsuario,
              descPosicion: usuario.descPosicion,
            }))
          )
        })
        .catch(console.error)
    }
  }

  /**
   * @description Genera el titulo en el campo de una tarea al seleccionar una nueva fecha.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.1.0, 21/12/2023
   * @param FechaTermino la fecha de inicio selecionada
   */
  const generarTituloTarea = (FechaTermino: Date | undefined) => {
    // Para tareas no manuales, el titulo incluye una fecha al final.
    // Este helper reemplaza el segmento de fecha cuando el usuario actualiza fechaTermino.
    const regexFecha = /(\d{1,2}\/\w{1,4}\/\d{2,4})/gm
    const regexFechaVacia = /(-{2}\/-{3}\/-{4})/gm
    const textoFechaVacia = '--/---/----'
    const testFechaTermino = regexFecha.test(getValues(`tareas.${index}.titulo`))
    const nuevoTituloTarea = getValues(`tareas.${index}.titulo`).replace(
      regexFecha,
      `${dayjs(FechaTermino).format(AppConstantes.FORMATO_FECHA)}`
    )

    if (FechaTermino && !getValues(`tareas.${index}.idTarea`)) {
      if (testFechaTermino) {
        setValue(`tareas.${index}.titulo`, nuevoTituloTarea)
      } else {
        const testFechaVacia = regexFechaVacia.test(getValues(`tareas.${index}.titulo`))
        if (testFechaVacia) {
          const tituloNombreYFecha = getValues(`tareas.${index}.titulo`).replace(
            textoFechaVacia,
            `${dayjs(FechaTermino).format(AppConstantes.FORMATO_FECHA)}`
          )
          setValue(`tareas.${index}.titulo`, tituloNombreYFecha)
        }
      }
    } else if (!FechaTermino) {
      const tituloSinFecha = getValues(`tareas.${index}.titulo`)?.replace(
        regexFecha,
        textoFechaVacia
      )
      setValue(`tareas.${index}.titulo`, tituloSinFecha)
    } else if (getValues(`tareas.${index}.idTarea`)) {
      if (testFechaTermino) {
        setValue(`tareas.${index}.titulo`, nuevoTituloTarea)
      }
    }
  }

  const idDivModalTareas = '#div-modal-tareas'

  return (
    <>
      <div className='mb-0' style={{ width: '15%' }}>
        <FormInput
          disabled={
            field.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_BAJA || !usuarioPermiso
          }
          maxLength={100}
          isObligatorio={true}
          nombreCampo={`tareas.${index}.titulo`}
          register={register}
          errors={errors}
          placeholder='Ej. Transporte de residuos'
        />
        {errors && errors?.tareas?.[index]?.titulo && (
          <p className='text-danger text-end fs-5'>{errors?.tareas[index]?.titulo?.message} </p>
        )}
      </div>
      <div className='mb-0' style={{ width: '25%' }}>
        <FormInput
          disabled={
            field.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_BAJA || !usuarioPermiso
          }
          maxLength={200}
          defaultValue={''}
          isObligatorio={true}
          nombreCampo={`tareas.${index}.descripcion`}
          register={register}
          errors={errors}
          placeholder='Ej. Verificar permisos y requisitos.'
        />
        {errors && errors?.tareas?.[index]?.descripcion && (
          <p className='text-danger text-end fs-5'>
            {errors?.tareas[index]?.descripcion?.message}{' '}
          </p>
        )}
      </div>
      <div className='mb-0' style={{ width: '15%' }}>
        <Controller
          control={control}
          name={`tareas.${index}.estatus`}
          render={({ field: { onChange, value } }) => (
            <FormSelect
              autoFocus={true}
              isDisabled={
                field.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_BAJA || !usuarioPermiso
              }
              isObligatorio={true}
              value={value}
              nombreCampo={`tareas.${index}.estatus`}
              elementos={
                field?.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_VENCIDA
                  ? catEstatusTarea?.filter(
                      (e) =>
                        e.valor !== AppConstantes.CVE_ESTATUS_TAREA_COMPLETADA &&
                        e.valor !== AppConstantes.CVE_ESTATUS_TAREA_CANCELADA
                    )
                  : field?.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_EN_PROCESO
                  ? catEstatusTarea?.filter(
                      (e) =>
                        e.valor !== AppConstantes.CVE_ESTATUS_TAREA_VENCIDA &&
                        e.valor !== AppConstantes.CVE_ESTATUS_TAREA_COMPLETADA
                    )
                  : field?.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_COMPLETADA
                  ? catEstatusTarea?.filter(
                      (e) => e.valor !== AppConstantes.CVE_ESTATUS_TAREA_VENCIDA
                    )
                  : catEstatusTarea?.filter(
                      (e) =>
                        e.valor !== AppConstantes.CVE_ESTATUS_TAREA_VENCIDA &&
                        e.valor !== AppConstantes.CVE_ESTATUS_TAREA_COMPLETADA &&
                        e.valor !== AppConstantes.CVE_ESTATUS_TAREA_CANCELADA
                    )
              }
              onChange={(e: { valor: string; etiqueta: string; ext5: string }) => {
                onChange(e)
                alertTareaCancelada(e)
              }}
              placeholder='Ej. En proceso'
              defaultValue={''}
              closeMenuOnScroll={(e) => e.target === document.querySelector(idDivModalTareas)}
              errors={errors}
            />
          )}
        />
        {errors && errors?.tareas?.[index]?.estatus && (
          <p className='text-danger text-end fs-5'>{errors?.tareas[index]?.estatus?.message} </p>
        )}
      </div>
      <div className='mb-0' style={{ width: '18%' }}>
        <Controller
          control={control}
          name={`tareas.${index}.idUsuarioAsignado`}
          render={({ field: { onChange, value } }) => (
            <FormSelectAutocomplete
              isDisabled={
                field.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_BAJA || !usuarioPermiso
              }
              autoFocus={true}
              nombreCampo={`tareas.${index}.idUsuarioAsignado`}
              errors={errors}
              isObligatorio={true}
              cargaOpciones={agregaOpcionesSelectUsuario}
              onChange={(e: { etiqueta: string; valor: string; descPosicion: string }) => {
                onChange(e)
              }}
              placeholder='Ej. Ana Martín'
              Componente={OpcionUsuarioSelect}
              value={value}
              closeMenuOnScroll={(e) => e.target === document.querySelector('#div-modal-tareas')}
            />
          )}
        />
        {errors && errors?.tareas?.[index]?.idUsuarioAsignado && (
          <p className='text-danger text-end fs-5'>
            {errors?.tareas[index]?.idUsuarioAsignado?.message}{' '}
          </p>
        )}
      </div>
      <div className='mb-0' style={{ width: '10%' }}>
        <Controller
          key={index}
          control={control}
          name={`tareas.${index}.fechaInicio`}
          render={({ field: { onChange, value } }) => (
            <FormReactDatePicker
              disabled={
                field.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_BAJA || !usuarioPermiso
              }
              required={true}
              name={`tareas.${index}.fechaInicio`}
              dateFormat='dd/MMM/yyyy'
              placeholderText='Ej. 22/feb/2022'
                onChange={(e: Date) => {
                  onChange(e)
                  // Si cambia la primer tarea generada por periodicidad, recalcula el resto.
                  index === 0 &&
                    getValues('periodicidad.valor') !== AppConstantes.CVE_PERIODICIDAD_CONDICIONANTE_UNICA &&
                    recalcularFechasInicio()
                }}
              isClearable={
                field.estatus?.valor !== AppConstantes.CVE_ESTATUS_TAREA_BAJA && usuarioPermiso
              }
              value={value}
              closeOnScroll={(e) => e.target === document.querySelector(idDivModalTareas)}
            />
          )}
        />
        {errors && errors?.tareas?.[index]?.fechaInicio && (
          <p className='text-danger text-end fs-5'>{errors?.tareas[index]?.fechaInicio?.message}</p>
        )}
      </div>
      <div className='mb-0' style={{ width: '10%' }}>
        <Controller
          key={index}
          control={control}
          name={`tareas.${index}.fechaTermino`}
          render={({ field: { onChange, value } }) => (
            <FormReactDatePicker
              disabled={
                field.estatus?.valor === AppConstantes.CVE_ESTATUS_TAREA_BAJA || !usuarioPermiso
              }
              minDate={
                getValues(`tareas.${index}.fechaInicio`)
                  ? new Date(getValues(`tareas.${index}.fechaInicio`))
                  : undefined
              }
              maxDate={fechaVencimiento ? new Date(fechaVencimiento) : undefined}
              required={true}
              value={value}
              name={`tareas.${index}.fechaTermino`}
              dateFormat='dd/MMM/yyyy'
              placeholderText='Ej. 22/mar/2022'
                onChange={(e: Date) => {
                  onChange(e)
                  !getValues(`tareas.${index}.isManual`) && generarTituloTarea(e)
                  // Si cambia la primer tarea generada por periodicidad, recalcula el resto.
                  index === 0 &&
                    getValues('periodicidad.valor') !== AppConstantes.CVE_PERIODICIDAD_CONDICIONANTE_UNICA &&
                    recalcularFechasInicio()
                }}
              isClearable={
                field.estatus?.valor !== AppConstantes.CVE_ESTATUS_TAREA_BAJA && usuarioPermiso
              }
              closeOnScroll={(e) => e.target === document.getElementById(idDivModalTareas)}
            />
          )}
        />
        {errors && errors?.tareas?.[index]?.fechaTermino && (
          <p className='text-danger text-end fs-5'>
            {errors?.tareas[index]?.fechaTermino?.message}
          </p>
        )}
      </div>
    </>
  )
}
